import React from "react";
import Marquee from "react-fast-marquee";
import { SimpleGrid, Flex, Text, AspectRatio } from "@chakra-ui/react";
import Image from "next/image";
import { type Banner, type Page as SanityPage, getPage, getAnnouncementBanner } from "lib/sanity";
import { type GetStaticProps, type NextPage } from "next";
import { SolutionsPartnersHero } from "components/solutions-partners-hero";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { getPageMetaImage } from "utils";
import { TextColumn, TextTitle } from "ui/text";
import { Button } from "ui/button";
import Balancer from "react-wrap-balancer";
import heroImage from "../../public/assets/company/solutions-partners/hero.png";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
};

const partners: Array<{ name: string; logo: string }> = [
  {
    name: "phData",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/d92063a36819bb2cd5ab999945ba3c3bfddedd5a-1672x425.png",
  },
  {
    name: "Media.Monks",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/574a1995b646fcd275c5ce90f13343972cae43e2-917x197.png",
  },
  {
    name: "DAS42",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/83a3749aced5f8a5204ad8c5f0ce80e8a2a23855-1096x228.png",
  },
  {
    name: "Slalom",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/7162945aa2078756b0f290b9c1f058aafe8d5a03-1500x400.png",
  },
  {
    name: "Hakkoda",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/224587ad10cdfc6af64139023cd9c8a8609f5f79-656x128.png",
  },
  {
    name: "Artefact",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/f2b64ae910b139e8bcbfe8db9045c881bdd5980a-494x102.png",
  },
  {
    name: "Blend",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/9ef2ce7ab0b180fc60200797d47ca1995de5d89b-5000x2000.png",
  },
  {
    name: "Tredence",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/97560aa155c39dee4073d85f5ec7dad323c0cb68-600x66.png",
  },
  {
    name: "Human37",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/ed9a32f518c3841383d64690378bc7f2f1eaeacf-1000x1000.webp",
  },
  {
    name: "Deloitte Digital",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/2a41f3d7ed5546d6ccc22016e43fcd561c0aed79-736x339.png",
  },
  {
    name: "Brooklyn Data Co.",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/0811da012715be75df9e0a457b776f282ecdf805-498x154.png",
  },
  {
    name: "Merkle",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/f4e90baa09efc14ffc0916ae1f06f5fb2f5c1628-1414x176.png",
  },
  {
    name: "Big Time Data",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/cf1411ab20b2c4730fe08f57f7596798a8342cc3-356x141.png",
  },
  {
    name: "Redkite",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/7027725c3e3ea690c9eaf75faf379dea5cce1af1-800x800.png",
  },
  {
    name: "Hiflylabs",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/c0edfb90a81581cc164d411c5695b4a4ad5cf812-937x266.png",
  },
  {
    name: "Tinuiti",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/ee229a9b6ac3dafa076b0425c82dfb10f21dff0b-161x58.svg",
  },
  {
    name: "Mozart Data",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/716109d5b2b19feb0f55cc5ad27889dcfd92fdef-1200x192.png",
  },
  {
    name: "Servian",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/398cea3020af7c1de9633ce69b539225d48e4530-500x79.png",
  },
  {
    name: "Aptitude 8",
    logo: "https://cdn.sanity.io/images/pwmfmi47/production/245458d007bc4d9c553107b130eea29e8969a75b-1442x226.webp",
  },
];

const SolutionsPartners: NextPage<Readonly<PageProps>> = ({ page, announcementBanner }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <SolutionsPartnersHero title={page.title} description={page.description} image={heroImage} />

      <Section bg="brand.backgroundSecondary.light" py={{ base: "biggest", md: "huge" }}>
        <SimpleGrid columns={[1, 1, 2, 4, 4]} spacing="bigger">
          <TextColumn
            titleAs="p"
            title="Business growth"
            description="Collaborate with Hightouch sales and receive access to opportunities to support Hightouch customers' success."
          />

          <TextColumn
            titleAs="p"
            title="Joint marketing"
            description="Amplify your brand with joint content, speaking opportunities, events, thought leadership and more."
          />

          <TextColumn
            titleAs="p"
            title="Leading tech"
            description="Build and deliver exceptional data and marketing solutions with the award-winning Reverse ETL platform."
          />

          <TextColumn
            titleAs="p"
            title="Your success"
            description="Leverage enablement resources, Slack access and technical support to ensure partner and client success."
          />
        </SimpleGrid>
      </Section>

      <Section center fullWidth>
        <Flex
          direction="column"
          gap={{ base: "big", md: "biggest" }}
          w="100%"
          align="center"
          justify="start"
          overflow="hidden"
        >
          <Text textAlign="center" textStyle={{ base: "headline3", md: "headline2" }}>
            Hightouch partners with
          </Text>

          <Marquee speed={32} gradientWidth="20px">
            {partners.map((partner) => {
              return (
                <AspectRatio
                  key={partner.name}
                  ratio={6 / 1}
                  w={{ base: "110px", md: "148px" }}
                  h={{ base: "32px", md: "44px" }}
                  mx={{ base: 6, md: 10 }}
                  pos="relative"
                >
                  <Image
                    fill
                    src={`${partner.logo}?w=300`}
                    alt={`${partner.name}.`}
                    sizes="100%"
                    style={{ objectFit: "contain" }}
                  />
                </AspectRatio>
              );
            })}
          </Marquee>
        </Flex>
      </Section>

      <Section center id="become-a-partner" my={0} pb={0}>
        <TextTitle
          align="center"
          size="lg"
          chip={{ text: "BECOME A PARTNER" }}
          title={<Balancer>Join our partner program today</Balancer>}
          description="Our Solutions Partners are an elite and innovative network of data experts that design, build and deliver exceptional data stacks and strategies."
          actions={
            <Button
              hasRightChevron
              href="https://docs.google.com/forms/d/e/1FAIpQLSex2W4-ZoRIN1E-rDDm4NPFAbllde88rj4q1uuBrrBUDCMPWA/viewform"
            >
              Become a partner
            </Button>
          }
          pb={sectionSpacingY}
        />
      </Section>
    </Page>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner] = await Promise.all([getPage("/solutions-partners"), getAnnouncementBanner()]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default SolutionsPartners;
